import React, { useEffect, useState } from "react";
import api from "../../../api/api";
import Swal from "sweetalert2";

import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
} from "@mui/material";

const STATUS_CHOICES = [
  "PICKUP",
  "LOADING",
  "DEPARTED",
  "TRANSIT",
  "ARRIVED",
  "READY",
  "CLOSED",
];

const ShippingTripList = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [editId, setEditId] = useState(null);

  const [editForm, setEditForm] = useState({
    name: "",
    vessel_name: "",
    status: "",
  });

  // ===========================
  // FETCH TRIPS (SAFE VERSION)
  // ===========================
  const fetchTrips = async (pageNumber = 1, searchValue = "") => {
    setLoading(true);

    try {
      const res = await api.get(
        `/shipping-trips/?page=${pageNumber}&search=${searchValue}`
      );

      const data = res.data;

      // 🔥 SAFE NORMALIZATION
      const tripsData = Array.isArray(data.results)
        ? data.results
        : Array.isArray(data)
        ? data
        : [];

      setTrips(tripsData);

      setTotalPages(
        data.count ? Math.ceil(data.count / 8) : 1
      );
    } catch (error) {
      console.log("API ERROR:", error);

      setTrips([]);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load shipping trips",
      });
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchTrips(page, search);
  }, [page, search]);

  // ===========================
  // EDIT HANDLING
  // ===========================
  const startEdit = (trip) => {
    setEditId(trip.id);

    setEditForm({
      name: trip.name,
      vessel_name: trip.vessel_name || "",
      status: trip.status,
    });
  };

  const handleChange = (e) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value,
    });
  };

  // ===========================
  // SAVE EDIT
  // ===========================
  const saveEdit = async (id) => {
    setActionLoading(true);

    try {
      await api.patch(`/shipping-trips/${id}/`, editForm);

      Swal.fire({
        icon: "success",
        title: "Updated",
        text: "Trip updated successfully",
        timer: 2000,
        showConfirmButton: false,
      });

      setEditId(null);
      fetchTrips(page, search);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update trip",
      });
    }

    setActionLoading(false);
  };

  // ===========================
  // DELETE
  // ===========================
  const deleteTrip = async (id) => {
    const result = await Swal.fire({
      title: "Delete Trip?",
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    setActionLoading(true);

    try {
      await api.delete(`/shipping-trips/${id}/`);

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Trip deleted successfully",
        timer: 2000,
        showConfirmButton: false,
      });

      fetchTrips(page, search);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete trip",
      });
    }

    setActionLoading(false);
  };

  // ===========================
  // SAFE CHECK
  // ===========================
  const safeTrips = Array.isArray(trips) ? trips : [];

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        background: "#f4f6f8",
        minHeight: "100vh",
      }}
    >
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Shipping Trips
      </Typography>

      {/* SEARCH */}
      <Box mb={3}>
        <TextField
          fullWidth
          placeholder="Search by trip name or vessel..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </Box>

      {/* LOADING */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>

              {/* HEADER */}
              <TableHead>
                <TableRow sx={{ background: "#0b1f3a" }}>
                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Trip Name
                  </TableCell>

                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Vessel
                  </TableCell>

                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Status
                  </TableCell>

                  <TableCell sx={{ color: "#fff", fontWeight: "bold" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              {/* BODY */}
              <TableBody>

                {safeTrips.length > 0 ? (
                  safeTrips.map((trip) => (
                    <TableRow key={trip.id} hover>

                      {editId === trip.id ? (
                        <>
                          {/* EDIT MODE */}
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              name="name"
                              value={editForm.name}
                              onChange={handleChange}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              name="vessel_name"
                              value={editForm.vessel_name}
                              onChange={handleChange}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              name="status"
                              value={editForm.status}
                              onChange={handleChange}
                            >
                              {STATUS_CHOICES.map((status) => (
                                <MenuItem key={status} value={status}>
                                  {status}
                                </MenuItem>
                              ))}
                            </TextField>
                          </TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => saveEdit(trip.id)}
                                disabled={actionLoading}
                              >
                                Save
                              </Button>

                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setEditId(null)}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          {/* VIEW MODE */}
                          <TableCell>{trip.name}</TableCell>
                          <TableCell>{trip.vessel_name || "N/A"}</TableCell>
                          <TableCell>{trip.status}</TableCell>

                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => startEdit(trip)}
                                disabled={actionLoading}
                              >
                                Edit
                              </Button>

                              <Button
                                color="error"
                                variant="outlined"
                                size="small"
                                onClick={() => deleteTrip(trip.id)}
                                disabled={actionLoading}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </>
                      )}

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No shipping trips found
                    </TableCell>
                  </TableRow>
                )}

              </TableBody>
            </Table>
          </TableContainer>

          {/* PAGINATION */}
          {totalPages > 1 && (
            <Box mt={3} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ShippingTripList;





